import React from 'react';

import colors from '~/assets/colors.module.scss';
import { useStyles } from '~/assets/styles';
import Chip from '~/components/core/Atomic/Chip/Chip';
import Container from '~/components/core/Atomic/Container';
import { FsButton } from '~/components/core/FsWrappers';
import Text from '~/components/core/TextComponents/Text';
import { AddIcon, ChevronRightIcon } from '~/components/icons';
import type { ProfileListItem } from '~/components/SystemConfiguration/Communications/PhoneConfigurationTab/phoneProfilesApi';

import CardDialog from '../../../../CardDialog';

import CreateProfileDialog from './CreateProfileDialog';

interface PhoneProfileListViewProps {
  profiles: ProfileListItem[];
  organizationId: number;
  onOpenProfile: (id: number) => void;
  reloadProfiles: () => Promise<unknown>;
  /**
   * Optional content rendered below the profiles list (still inside this view's outer container).
   * Used by the tab to slot in `<OrgPhoneSurveyCard>` when the phone-survey feature flag is on.
   */
  children?: React.ReactNode;
}

const PhoneProfileListView: React.FC<PhoneProfileListViewProps> = ({
  profiles,
  organizationId,
  onOpenProfile,
  reloadProfiles,
  children,
}) => {
  const { classes } = useStyles();
  const [isCreateOpen, setIsCreateOpen] = React.useState(false);

  const handleCreated = async (newProfileId: number) => {
    await reloadProfiles();
    setIsCreateOpen(false);
    onOpenProfile(newProfileId);
  };

  return (
    <div className="bg-slate-100 pt-32">
      <Container maxWidth="md">
        <CardDialog
          title="Phone Profiles"
          width="md"
          action={
            <FsButton color="primary" onClick={() => setIsCreateOpen(true)}>
              <>
                <AddIcon className={classes.leftButtonIcon} iconColor={colors.buttonLink} />
                Create profile
              </>
            </FsButton>
          }
        >
          <div className="flex flex-col">
            {profiles.map((profile) => (
              <button
                key={profile.id}
                type="button"
                onClick={() => onOpenProfile(profile.id)}
                className="hover:bg-slate-50 group flex w-full cursor-pointer items-center justify-between border-0 border-b border-solid border-slate-200 bg-transparent px-12 py-16 text-left transition-colors last:border-b-0"
              >
                <div className="flex items-center gap-12">
                  <Text variant={Text.VARIANTS.LG} weight={Text.WEIGHTS.SEMI_BOLD}>
                    {profile.name}
                  </Text>
                  {profile.is_default ? <Chip size="small" label="Default" /> : null}
                </div>
                <span className="inline-flex transition-transform group-hover:translate-x-4">
                  <ChevronRightIcon />
                </span>
              </button>
            ))}
          </div>
        </CardDialog>
        {children}
        <CreateProfileDialog
          isOpen={isCreateOpen}
          organizationId={organizationId}
          onClose={() => setIsCreateOpen(false)}
          onCreated={handleCreated}
        />
      </Container>
    </div>
  );
};

export default PhoneProfileListView;
